import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Palette, Upload, Music, Image as ImageIcon } from "lucide-react";

const COLORS = [
  { name: "Ocean", value: "hsl(174 60% 94%)" },
  { name: "Sunset", value: "hsl(24 90% 94%)" },
  { name: "Forest", value: "hsl(150 40% 94%)" },
  { name: "Lavender", value: "hsl(260 60% 96%)" },
  { name: "Cloud", value: "hsl(210 40% 98%)" },
];

const FONTS = [
  { name: "Modern", value: "Figtree" },
  { name: "Playful", value: "Comic Neue" },
  { name: "Classic", value: "Georgia" },
];

export default function Customize() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState({});

  const load = async () => { const { data } = await api.get("/preferences"); setPrefs(data); };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      const { data } = await api.patch("/preferences", patch);
      setPrefs(data);
      toast.success("Saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const readFile = (f, cb, cap) => {
    if (!f) return;
    if (f.size > cap) return toast.error(`File too large — under ${Math.round(cap/1024/1024)} MB please.`);
    const r = new FileReader();
    r.onload = () => cb(r.result);
    r.readAsDataURL(f);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="customize-page">
      <div>
        <div className="chip mb-2">Make it yours</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Customize your dashboard</h1>
        <p className="text-slate-600 text-sm">Pick a color, upload a photo, add a song. Small changes, big vibe.</p>
      </div>

      <section className="p-6 rounded-3xl bg-white border border-slate-200 space-y-3">
        <div className="flex items-center gap-2"><Palette size={18} className="text-teal-700" /><h2 className="font-display font-semibold">Background color</h2></div>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((c) => (
            <button key={c.value} data-testid={`cust-color-${c.name.toLowerCase()}`} onClick={()=>save({ background_color: c.value })}
              style={{ background: c.value }}
              className={`px-4 py-2 rounded-full border ${prefs.background_color === c.value ? "border-teal-700 ring-2 ring-teal-200" : "border-slate-200"} text-sm font-semibold text-slate-700`}>
              {c.name}
            </button>
          ))}
        </div>
      </section>

      <section className="p-6 rounded-3xl bg-white border border-slate-200 space-y-3">
        <div className="flex items-center gap-2"><ImageIcon size={18} className="text-teal-700" /><h2 className="font-display font-semibold">Background image</h2></div>
        <label className="px-4 py-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center gap-2 cursor-pointer w-fit">
          <Upload size={16} className="text-slate-500" />
          <span className="text-sm text-slate-600">Choose image (under 2 MB)</span>
          <input data-testid="cust-bg-file" type="file" accept="image/*" onChange={(e)=>readFile(e.target.files?.[0], (data)=>save({ background_image: data }), 2 * 1024 * 1024)} className="hidden" />
        </label>
        {prefs.background_image && (
          <div className="mt-2 flex items-center gap-2">
            <img src={prefs.background_image} alt="" className="w-32 h-20 object-cover rounded-lg border border-slate-200" />
            <button data-testid="cust-bg-remove" onClick={()=>save({ background_image: "" })} className="text-sm text-red-500 font-semibold">Remove</button>
          </div>
        )}
      </section>

      <section className="p-6 rounded-3xl bg-white border border-slate-200 space-y-3">
        <div className="flex items-center gap-2"><ImageIcon size={18} className="text-teal-700" /><h2 className="font-display font-semibold">Avatar</h2></div>
        <label className="px-4 py-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center gap-2 cursor-pointer w-fit">
          <Upload size={16} className="text-slate-500" />
          <span className="text-sm text-slate-600">Choose avatar (under 1 MB)</span>
          <input data-testid="cust-avatar-file" type="file" accept="image/*" onChange={(e)=>readFile(e.target.files?.[0], (data)=>save({ avatar: data }), 1 * 1024 * 1024)} className="hidden" />
        </label>
        {prefs.avatar && <img src={prefs.avatar} alt="" className="w-16 h-16 rounded-full object-cover border border-slate-200 mt-2" />}
      </section>

      <section className="p-6 rounded-3xl bg-white border border-slate-200 space-y-3">
        <div className="flex items-center gap-2"><Music size={18} className="text-teal-700" /><h2 className="font-display font-semibold">Focus music (YouTube or SoundCloud link)</h2></div>
        <input data-testid="cust-music" type="url" defaultValue={prefs.music_url || ""} onBlur={(e)=>save({ music_url: e.target.value })}
          placeholder="Paste a lofi playlist URL…"
          className="w-full px-4 py-3 rounded-xl border border-slate-200" />
        <p className="text-xs text-slate-500">Shows as a little "Play music" button on your Today screen.</p>
      </section>
    </div>
  );
}
